import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MessageCircle, PhoneCall, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useConfig } from "@/lib/auth";
import { fmtNum, fmtPrice, vTitle, vDesc } from "@/lib/format";
import { track, visitorId, utm } from "@/lib/track";
import { Gallery } from "@/components/xd/Gallery";
import { SimilarRequest } from "@/components/xd/SimilarRequest";
import { Loader, Empty, Field } from "@/components/xd/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export const SpecGrid = ({ v }) => {
  const { t } = useLang();
  const rows = [
    v.trim && ["spec.trim", <span dir="ltr">{v.trim}</span>],
    ["spec.year", <span className="ltr-num">{v.year}</span>],
    ["spec.mileage", <><span className="ltr-num">{fmtNum(v.mileage)}</span> {t("unit.km")}</>],
    ["spec.hand", <span className="ltr-num" data-testid="spec-hand-value">{v.hand}</span>],
    ["spec.fuel", t(`fuel.${v.fuel}`)],
    ["spec.gearbox", t(`gear.${v.gearbox}`)],
    ["spec.body_type", t(`body.${v.body_type}`)],
    ["spec.color", v.color],
    v.interior_color && ["spec.interior_color", v.interior_color],
    ["spec.engine", v.engine || "—"],
    ["spec.horsepower", v.horsepower ? <span className="ltr-num">{v.horsepower}</span> : "—"],
  ].filter(Boolean);
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/5 border border-white/5" data-testid="vehicle-specs">
      {rows.map(([k, val]) => (
        <div key={k} className="bg-black p-4 min-w-0">
          <dt className="text-xs text-zinc-500">{t(k)}</dt>
          <dd className="mt-1.5 text-sm text-white break-words">{val}</dd>
        </div>
      ))}
    </dl>
  );
};

const CallbackDialog = ({ v, className = "btn-ghost w-full", testid = "callback-open-button", iconOnly = false }) => {
  const { t, tErr, lang } = useLang();
  const { content } = useConfig();
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "", consent: false });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/inquiries", { type: "callback", vehicle_id: v.id, visitor_id: visitorId(), lang, utm: utm(), ...form });
      setDone(data.inquiry_code);
    } catch (err) {
      toast.error(tErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className={className} aria-label={t("vehicle.callback")} data-testid={testid}><PhoneCall size={17} />{!iconOnly && <span>{t("vehicle.callback")}</span>}</button>
      </DialogTrigger>
      <DialogContent className="bg-black border-white/10 text-white" data-testid="callback-dialog">
        <DialogHeader><DialogTitle className="text-start">{t("vehicle.callback")}</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3 py-4" data-testid="callback-success">
            <p className="text-zinc-300">{t("callback.done")}</p>
            <p className="text-sm text-zinc-500">{t("callback.ref")} <span className="ltr-num text-white" data-testid="callback-inquiry-code">{done}</span></p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label={t("form.name")}><Input required className="xd-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="callback-name-input" /></Field>
            <Field label={t("form.phone")}><Input required type="tel" dir="ltr" className="xd-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="callback-phone-input" /></Field>
            <Field label={t("callback.time")}><Input className="xd-input" value={form.preferred_time} placeholder={t("callback.time_ph")} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="callback-time-input" /></Field>
            <label className="flex items-start gap-3 text-sm text-zinc-400">
              <Checkbox checked={form.consent} onCheckedChange={(c) => setForm({ ...form, consent: !!c })} data-testid="callback-consent-checkbox" className="mt-0.5" />
              <span>{content.callback_consent?.[lang]}</span>
            </label>
            <p className="text-xs text-zinc-500" data-testid="callback-legal-links">
              {t("legal.more")} <Link to="/privacy" className="underline hover:text-white" data-testid="callback-privacy-link">{t("page.privacy")}</Link> · <Link to="/terms" className="underline hover:text-white" data-testid="callback-terms-link">{t("page.terms")}</Link>
            </p>
            <button disabled={busy || !form.consent} className="btn-xd w-full" data-testid="callback-submit-button">{t("callback.send")}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EnquiryActions = ({ v, className = "btn-xd w-full !min-h-[3.25rem]", testid = "whatsapp-enquire-button" }) => {
  const { t, tErr, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const whatsapp = async () => {
    setBusy(true);
    const w = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? null : window.open("", "_blank");
    try {
      const { data } = await api.post("/public/inquiries", { type: "whatsapp", vehicle_id: v.id, visitor_id: visitorId(), lang, utm: utm() });
      if (w) w.location.href = data.whatsapp_url; else window.location.href = data.whatsapp_url;
    } catch (e) {
      w?.close();
      toast.error(tErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={whatsapp} disabled={busy} className={className} data-testid={testid}>
      <MessageCircle size={19} className="shrink-0" /><span>{t("vehicle.whatsapp")}</span>
    </button>
  );
};

const MobileContact = ({ v, sold, actionsRef }) => {
  const { t } = useLang();
  return (
    <div className="lg:hidden space-y-4" data-testid="vehicle-mobile-contact">
      <div>
        <p className="eyebrow">{sold ? t("vehicle.last_price") : t("vehicle.price")}</p>
        <p className="mt-2 text-3xl font-semibold chrome-text ltr-num" data-testid="vehicle-price-mobile">{fmtPrice(v.price)}</p>
      </div>
      {sold ? <><p className="text-sm text-zinc-400" data-testid="vehicle-sold-note-mobile">{t("vehicle.sold_note")}</p><SimilarRequest v={v} suffix="-mobile" className="pt-2 max-w-xl" /></> : (
        <div ref={actionsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl">
          <EnquiryActions v={v} className="btn-xd w-full !min-h-[3rem] !px-4 !text-[15px] whitespace-nowrap" testid="whatsapp-enquire-button-top" />
          <CallbackDialog v={v} className="btn-ghost w-full !min-h-[3rem] !px-4 !text-[15px] whitespace-nowrap !border-white/40 !bg-white/[0.04] hover:!border-white/70" testid="callback-open-button-top" />
        </div>
      )}
      <p className="text-xs text-zinc-600">{t("vehicle.price_note")}</p>
    </div>
  );
};

const useOutOfView = () => {
  const [el, setEl] = useState(null);
  const [out, setOut] = useState(false);
  useEffect(() => {
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setOut(!e.isIntersecting && e.boundingClientRect.top < 0));
    io.observe(el);
    return () => { io.disconnect(); };
  }, [el]);
  return [setEl, out];
};

export default function VehicleDetail() {
  const { code } = useParams();
  const { t, lang } = useLang();
  const [v, setV] = useState(undefined);
  const [actionsRef, scrolledPast] = useOutOfView();
  const showBar = !!v && v.availability !== "sold";
  useEffect(() => {
    document.body.classList.toggle("has-sticky-bar", showBar);
    return () => { document.body.classList.remove("has-sticky-bar"); };
  }, [showBar]);
  const viewed = useRef(null);
  useEffect(() => {
    api.get(`/public/vehicles/${code}`).then((r) => {
      setV(r.data);
      if (viewed.current !== code) { viewed.current = code; track("vehicle_view", { vehicle_id: r.data.id }); }
    }).catch(() => setV(null));
  }, [code]);
  if (v === undefined) return <Loader />;
  if (!v) return <div className="mx-auto max-w-3xl px-6 py-24"><Empty text={t("vehicle.not_found")} /></div>;
  const sold = v.availability === "sold";
  return (
    <div className="pb-10 md:pb-0">
      <div className="mx-auto max-w-7xl md:px-8 md:pt-8">
        <Link to={sold ? "/sold" : "/inventory"} className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-5" data-testid="vehicle-back-link"><ArrowRight size={16} />{t(sold ? "nav.sold" : "nav.inventory")}</Link>
        <Gallery photos={v.photos} title={vTitle(v, lang)} status={v.availability} />
      </div>
      <div className="mx-auto max-w-7xl px-5 md:px-8 mt-8 md:mt-10 grid lg:grid-cols-[1fr_380px] gap-8 lg:gap-12">
        <div className="space-y-8 lg:space-y-10 min-w-0">
          <div>
            <p className="eyebrow ltr-num">{v.code}</p>
            <h1 className="mt-3 text-3xl sm:text-5xl font-semibold tracking-tight chrome-text break-words" data-testid="vehicle-title">{vTitle(v, lang)}</h1>
          </div>
          <MobileContact v={v} sold={sold} actionsRef={actionsRef} />
          <SpecGrid v={v} />
          <div>
            <h2 className="text-base md:text-lg font-medium mb-4">{t("vehicle.description")}</h2>
            <p className="text-zinc-300 leading-relaxed whitespace-pre-line" data-testid="vehicle-description">{vDesc(v, lang)}</p>
          </div>
        </div>
        <aside className="hidden lg:block lg:sticky lg:top-28 h-fit xd-panel p-6 space-y-5" data-testid="vehicle-price-panel">
          <p className="eyebrow">{sold ? t("vehicle.last_price") : t("vehicle.price")}</p>
          <p className="text-4xl font-semibold chrome-text ltr-num" data-testid="vehicle-price">{fmtPrice(v.price)}</p>
          <div className="xd-line" />
          {sold ? <><p className="text-sm text-zinc-400" data-testid="vehicle-sold-note">{t("vehicle.sold_note")}</p><SimilarRequest v={v} /></> : (
            <div className="space-y-3"><EnquiryActions v={v} /><CallbackDialog v={v} className="btn-ghost w-full !border-white/40 hover:!border-white/70" /></div>
          )}
          <p className="text-xs text-zinc-600">{t("vehicle.price_note")}</p>
        </aside>
      </div>
      {!sold && scrolledPast && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-black/90 backdrop-blur-md border-t border-white/10 px-4 pt-3 safe-bottom grid grid-cols-[1fr_auto] gap-3 fade-up" data-testid="sticky-enquiry-bar">
          <EnquiryActions v={v} className="btn-xd w-full !min-h-[3rem]" testid="whatsapp-enquire-button-sticky" />
          <CallbackDialog v={v} iconOnly className="btn-ghost !min-h-[3rem] !w-14 !px-0 !border-white/40" testid="callback-open-button-sticky" />
        </div>
      )}
    </div>
  );
}
